import BrandMark from "../components/brand/BrandMark";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Plus, Phone, User } from "lucide-react";
import { useI18n } from "../i18n";

const API = '/api';

const VehicleCreate = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    plate_number: "",
    primary_contact: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/vehicles`, formData);
      toast.success(t("owner.vcreate.success"));
      navigate(`/checkout/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || t("owner.vcreate.error");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link to="/dashboard" data-testid="back-to-dashboard">
              <Button variant="ghost" size="icon" className="text-slate-600">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <BrandMark size="sm" asLink={false} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t("owner.vcreate.title")}
          </h1>
          <p className="text-slate-600 mt-1">{t("owner.vcreate.subtitle")}</p>
        </div>

        <Card className="border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("owner.vcreate.info_title")}
            </CardTitle>
            <CardDescription>
              {t("owner.vcreate.info_desc")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Vehicle Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700">
                    {t("owner.vcreate.name_label")}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder={t("owner.vcreate.name_ph")}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 bg-slate-50 border-slate-200"
                    data-testid="vehicle-name-input"
                  />
                  <p className="text-xs text-slate-500">
                    Un nombre que te ayude a identificar este vehículo
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="plate_number" className="text-slate-700">
                    Placa del vehículo (opcional)
                  </Label>
                  <Input
                    id="plate_number"
                    name="plate_number"
                    placeholder="Ej: ABC-123"
                    value={formData.plate_number}
                    onChange={handleChange}
                    className="h-12 bg-slate-50 border-slate-200"
                    data-testid="vehicle-plate-input"
                  />
                  <p className="text-xs text-slate-500">
                    {t("owner.vcreate.plate_hint")}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="primary_contact" className="text-slate-700 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {t("owner.vcreate.phone_label")}
                  </Label>
                  <Input
                    id="primary_contact"
                    name="primary_contact"
                    type="tel"
                    placeholder="987654321"
                    value={formData.primary_contact}
                    onChange={handleChange}
                    required
                    className="h-12 bg-slate-50 border-slate-200"
                    data-testid="vehicle-primary-contact-input"
                  />
                  <p className="text-xs text-slate-500">
                    {t("owner.vcreate.phone_hint")}
                  </p>
                </div>
              </div>

              {/* iter64 — Contactos de emergencia removidos. La alerta viaja como push al propietario. */}
              {/* Submit */}
              <div className="border-t border-slate-100 pt-6">
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                  disabled={loading}
                  data-testid="create-vehicle-submit-btn"
                >
                  {loading ? (
                    t("owner.vcreate.submitting")
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      {t("owner.vcreate.submit")}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default VehicleCreate;

